import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from '@mui/material/Stack';
import LoginState from './LoginState';
import CollapsibleCard from './CollapsibleCard';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CircularProgress } from '@mui/material';
import Receipt from './Receipt.js';
import moment from 'moment';
import PaymentIcon from '@mui/icons-material/Payment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ModalEmailLog from './ModalEmailLog';
import Tooltip from '@mui/material/Tooltip';

export default function OrderPayment({ order }) {

  const [paymentDetails, setPaymentDetails] = useState();
  const [fees, setFees] = useState({});
  const [originalFees, setOriginalFees] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceDates, setInvoiceDates] = useState({});
  const [toast, setToast] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showNewFeeModal, setShowNewFeeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [showEmailLogs, setShowEmailLogs] = useState([]);

  const setFeeItem = (paymentId, value) => {
    setFees(prevFees => ({
      ...prevFees,
      [paymentId]: value
    }));
  };
  const setOriginalFeeItem = (paymentId, value) => {
    setOriginalFees(prevFees => ({
      ...prevFees,
      [paymentId]: value
    }));
  };
  const setInvoiceDateItem = (paymentId, value) => {
    setInvoiceDates(prevDates => ({
      ...prevDates,
      [paymentId]: value
    }));
  };
  const setShowEmailLogItem = (paymentId, value) => {
    setShowEmailLogs(prevState => ({
      ...prevState,
      [paymentId]: value
    }));
  };

  useEffect(() => {
    if (!paymentDetails) {
      const _getPaymentDetails = async () => {
        let response = await axios.get('/getpaymentdetails', {
          params: {
            pOrderID: order.orderID,
            pUserName: LoginState.userName
          }
        });
        if (response.data) {
          setPaymentDetails(response.data);
          for(var i=0; i<response.data.length; i++)
          {
            var pd = response.data[i];
            setInvoiceDateItem(pd.paymentID, pd.invoiceDate);
            setFeeItem(pd.paymentID, pd.paymentLinkFee);
            setOriginalFeeItem(pd.paymentID, pd.paymentLinkFee);
          }

        } else {
          setError('Something went wrong fetching payment data')
        }
      }

      _getPaymentDetails();
    }
    getPaymentLogs();
  }, [paymentDetails, order]);

  const getPaymentLogs = async () => {
    let response = await axios.get('/getPaymentEmailLogs', {
      params: { pOrderID: order.orderID, }
    });
    if (response.data) { setEmailLogs(response.data); }
  }

  const updateInvoiceDate = async (paymentID, formattedDate) => {
    await axios.get('/setinvoicedate', {
      params: {
        pPaymentID: paymentID,
        pDateValue: formattedDate
      }
    });
  }

  function getLogs(paymentID) {
    var emailList = [];
    for(var i=0; i<emailLogs.length; i++)
    {
      if(emailLogs[i].paymentID === paymentID) { emailList.push(emailLogs[i]); }
    }
    return emailList;
  }

  function delay(time) {
    return new Promise(resolve => setTimeout(resolve, time));
  }

  function parseCurrency(value) {
    if (value == null) return null;

    return parseFloat(
      value
        .toString()
        .replace(/[^0-9.-]+/g, "")
    );
  }

  function formatCurrency(value) {
    if (value === null || value === undefined || value === "") return "";
    const number = Number(value);
    if (Number.isNaN(number)) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  }

  window.markPaid = function () {
    // the payment success page will call us to refresh
    // the page and show the updated payment status
    window.location.reload();
  }

  const markPaymentReceived = async (paymentID) => {
    await axios.get('/markpaymentreceived', {
      params: { pPaymentID: paymentID}
    });
    window.markPaid();
  }

  const submitPaymentRequest = async (paymentID) => {
    // call code that will send the payment request email for the set fee amount.
    setShowConfirmModal(null);
    setLoading(true);
    setOriginalFeeItem(paymentID, fees[paymentID]); 
    const response = axios.get('/paymentlink', {
      params: {
        pFee: fees[paymentID],
        pPaymentID: paymentID,
        pUserName: LoginState.userName,
      }
    });
    var resultData = (await response).data;
    if (resultData) {
      if(resultData.includes("Success")) {
        setToast("Payment Email Sent to Borrower");
        setTimeout(() => { setToast(null); }, 10000);
        getPaymentLogs();
      }
      else
        setError("Error: " + resultData);
    }
    setLoading(false);
  }

  const updateFee = async (pdID, fee) => {
    const response = axios.get('/setFee', {
      params: {
        pPaymentID: pdID,
        pFee: fee,
      }
    });
    var resultData = (await response).data;
    if (resultData) { 
      setOriginalFeeItem(pdID, fee); 
    }
  }

  const addNewFeeToOrder = async () => {
    setShowNewFeeModal(false);
    const response = axios.get('/addFee', {
      params: { pOrderID: order.orderID }
    });
    var resultData = (await response).data;
    if (resultData) {  setPaymentDetails(null); } // this should force reload data
  }

  if (!paymentDetails) {
    return <Stack justifyContent="center" alignItems="center" p={4}>
      <CircularProgress />
    </Stack>
  }
  else if (LoginState.userRole === 'Admin' || LoginState.userRole === 'Portal') {
    return (
      <CollapsibleCard title="payment">
        {paymentDetails.map((pd) => (
          <React.Fragment key={pd.paymentID}>
            {(pd.paid === "Pending" || pd.paid === "At Closing" || pd.paid === "---" || pd.paid === "") ? (
              <React.Fragment>
                <Stack direction="row" spacing={2} justifyContent="left" alignItems="center" sx={{mb:2}}>
                  {LoginState.userRole === 'Admin' && (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label={'Invoice Date'}
                        value={invoiceDates[pd.paymentID] ? moment(invoiceDates[pd.paymentID]) : null}
                        onChange={val => {
                          const formatted = moment(val).format('MM/DD/YYYY');
                          setInvoiceDateItem(pd.paymentID, formatted);
                          updateInvoiceDate(pd.paymentID, formatted);
                        }}
                        renderInput={(params) => <TextField {...params} size="small"/>}
                      />
                    </LocalizationProvider>
                  )}
                  <Typography variant="body1" style={{ position:'relative', top:'-4px', textDecoration: ((!fees[pd.paymentID] || error || 
                    (fees[pd.paymentID] && parseCurrency(fees[pd.paymentID]) === parseCurrency(pd.fee))) ? 'none' : 'line-through') }}>
                    Default Fee:<br/>{pd.fee || "---"}
                  </Typography>
                  <TextField
                    size="small"
                    label="Custom Fee"
                    type="number"
                    name="fee"
                    disabled={LoginState.userRole !== 'Admin'}
                    value={fees[pd.paymentID] ?? ""}
                    onChange={(e) => setFeeItem(pd.paymentID, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end"></InputAdornment>
                    }}
                  />
                  {LoginState.userRole === 'Admin' && (
                    <Button variant="contained" size="small" style={{marginRight:'5px'}}
                      disabled={originalFees[pd.paymentID] === fees[pd.paymentID]}
                      onClick={()=>updateFee(pd.paymentID, fees[pd.paymentID])}>
                      Update
                    </Button>
                  )}
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="right" alignItems="right" sx={{ mt: 2 }}>
                  {getLogs(pd.paymentID).length > 0 && (
                    <React.Fragment>
                      <Button variant="outlined" size="small" onClick={() => setShowEmailLogItem(pd.paymentID, true)}>
                        <i
                          className="bi bi-envelope-check"
                          style={{
                            color: 'green',
                            fontSize: '20px',
                            position: 'relative',
                            marginRight:'5px'
                          }}
                        /> View Logs
                      </Button>
                    </React.Fragment>
                  )}
                  <Button
                    style={{marginRight:'15px', marginLeft:'15px'}}
                    disabled={((pd.fee === "---" || pd.fee === "$0.00") && !fees[pd.paymentID]) || error || loading || pd.intentToProceedDate === "---"}
                    variant="contained"
                    onClick={() => setShowConfirmModal(pd.paymentID)}
                  >{getLogs(pd.paymentID).length > 0 && 'Re-'}Send Payment Request</Button>
                  {LoginState.userRole === 'Admin' && (
                    <Button
                      style={{marginLeft:'0px'}}
                      disabled={(pd.fee === "---" && !fees[pd.paymentID]) || error || loading}
                      onClick={() => markPaymentReceived(pd.paymentID)}
                    >Mark as Paid</Button>
                  )}
                </Stack>
                <hr style={{marginBottom:'30px', marginTop:'20px', border: "none", borderTop: "1px solid lightgray"}} />
                {pd.intentToProceedDate === "---" && (
                  <Alert severity="warning" sx={{mb: 4}}>Payments Disabled Until Loan File's "Intent to Proceed" Date Is Set</Alert>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Alert severity={pd.paid && pd.paid !== "Pending" ? "success" : "warning"} 
                  sx={{ mb: 3 }} 
                  action={pd.paid && <Receipt orderID={order.orderID} fee={originalFees[pd.paymentID]} />}>
                  <strong>{pd.paid ? "Paid" : "Not Paid"}</strong> 
                  {(pd.paid && pd.paymentDate !== "---") && (" " + pd.paymentDate + " (" + formatCurrency(pd.paymentLinkFee) + ")")}
                </Alert>
              </React.Fragment>
            )}

            {/* CONFIRMATION MODAL */}
            <Dialog
              open={showConfirmModal || false}
              onClose={() => setShowConfirmModal(null)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                Confirm Send Payment Email
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" style={{textAlign:'center'}}>
                  Please confirm that you intend to send the <br/>
                  payment request email to the borrower.<br/>
                  <small style={{color:'#8c221b'}}>(Check that the payment amount: {formatCurrency(fees[showConfirmModal])} is correct and final)</small>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowConfirmModal(null)}>Cancel</Button>
                <Button onClick={() => submitPaymentRequest(showConfirmModal)} autoFocus>Send</Button>
              </DialogActions>
            </Dialog>

            {/* NEW FEE CONFIRMATION MODAL */}
            <Dialog
              open={showNewFeeModal}
              onClose={() => setShowNewFeeModal(null)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                Confirm Add New Fee
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" style={{textAlign:'center'}}>
                  Please confirm that you intend to add a new fee to this appraisal order. <br/>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowNewFeeModal(false)}>Cancel</Button>
                <Button onClick={() => addNewFeeToOrder()} autoFocus>Add Fee</Button>
              </DialogActions>
            </Dialog>

            {/* EMAIL LOG MODAL */}
            <ModalEmailLog
              show={showEmailLogs[pd.paymentID]}
              emailLog={getLogs(pd.paymentID)}
              onDecline={() => setShowEmailLogItem(pd.paymentID, false)}
            />
          </React.Fragment>
        ))}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {toast && <Alert severity="success" sx={{mb: 2}}>{toast}</Alert>}

        {LoginState.userRole === 'Admin' && (
          <Tooltip placement="bottom" title={<span>Add Additional Fee</span>}>
            <Button
              style={{marginLeft:'10px'}}
              disabled={paymentDetails.length < 1 || paymentDetails[0].intentToProceedDate === "---"}
              variant="outlined"
              onClick={() => setShowNewFeeModal(true)}
            >+</Button>
          </Tooltip>
        )}
      </CollapsibleCard>
    )
  }
}